import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    CurrencyDollar, Clock, CheckCircle, Receipt, Calendar, DownloadSimple
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { localDateKey, formatDayLabel as fmtDay, formatDate } from '../lib/dateLocal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);

const PAYMENT_LABELS = { cash: 'Efectivo', credit: 'T. Crédito', debit: 'T. Débito', transfer: 'Transferencia' };

const MyCommissionsPage = () => {
    const { user, can } = useAuth();
    const canViewHistory = can('commissions.payment_history_view_own');
    const canDownloadHistory = can('commissions.payment_history_download_own');
    const [pending, setPending] = useState({ sales: [], count: 0, total_sales_amount: 0, commission_percent: 0, estimated_commission: 0 });
    const [paidHistory, setPaidHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const pendingPromise = axios.get(`${API}/my-pending-sales`, { headers: authHeaders() });
                const historyPromise = canViewHistory
                    ? axios.get(`${API}/commissions`, { headers: authHeaders() })
                    : Promise.resolve({ data: [] });
                const [p, h] = await Promise.all([pendingPromise, historyPromise]);
                setPending(p.data);
                setPaidHistory((h.data || []).filter(c => c.status === 'paid'));
            } catch (err) {
                toast.error('Error al cargar comisiones');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Group pending sales by day in LOCAL TIMEZONE (GMT-6) — fixes the bug
    // where a sale at 22:30 local (UTC next day) was grouped on the wrong day.
    const byDay = pending.sales.reduce((acc, s) => {
        const day = localDateKey(s.created_at);
        if (!acc[day]) acc[day] = { date: day, count: 0, certificates_count: 0, amount: 0, folios: [], items: [] };
        acc[day].count += 1;
        acc[day].certificates_count += (s.folios?.length || 0);
        acc[day].amount += s.total_amount || 0;
        acc[day].folios.push(...(s.folios || []));
        acc[day].items.push(s);
        return acc;
    }, {});
    const days = Object.values(byDay).sort((a, b) => b.date.localeCompare(a.date));

    const totalSalesCount = days.reduce((sum, d) => sum + d.count, 0);
    const totalCertsCount = days.reduce((sum, d) => sum + d.certificates_count, 0);

    const totalPaid = paidHistory.reduce((sum, c) => sum + (c.net_amount || 0), 0);

    const downloadPdf = async (commissionId, sellerName) => {
        try {
            const res = await axios.get(`${API}/commissions/${commissionId}/pdf`, {
                headers: authHeaders(),
                responseType: 'blob'
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `comision-${(sellerName || 'vendedor').replace(/\s/g, '_')}-${commissionId.slice(0, 8)}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            toast.error('No se pudo descargar el PDF');
        }
    };

    if (loading) return <div className="p-8 text-center text-[#8B8474]">Cargando...</div>;

    return (
        <div className="space-y-6 animate-fade-up max-w-[1200px]" data-testid="my-commissions-page">
            <div className="pb-6 border-b border-[#ECE3D2]">
                <div className="flex items-center gap-2 text-xs mb-2">
                    <span className="text-[#8B8474]">Mi cuenta</span>
                    <span className="text-[#C7BCA5]">/</span>
                    <span className="text-[#1F2D4B] font-medium">Comisiones</span>
                </div>
                <h1 className="font-display text-3xl font-bold text-[#1F2D4B]">Mis comisiones</h1>
                <p className="text-sm text-[#4B5878] mt-1.5">
                    Hola, <span className="font-semibold">{user?.name}</span>. Aquí está el resumen de tu producción y comisiones.
                </p>
            </div>

            {/* KPIs — sólo cosas de COMISIÓN (sin totales de venta ni % visible) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white border border-[#ECE3D2] rounded-lg p-5" data-testid="kpi-pending-count">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold">Pendientes</span>
                        <Clock size={18} weight="duotone" className="text-[#FB7185]" />
                    </div>
                    <p className="font-display text-3xl font-bold text-[#1F2D4B]">
                        {pending.count} <span className="text-lg text-[#8B8474] font-medium">{pending.count === 1 ? 'venta' : 'ventas'}</span>
                    </p>
                    <p className="text-xs text-[#4B5878] mt-1">
                        {totalCertsCount} {totalCertsCount === 1 ? 'certificado' : 'certificados'}
                        {pending.count_pending_validation ? ` · ${pending.count_pending_validation} por validar` : ''}
                    </p>
                </div>
                <div className="bg-[#1F2D4B] border border-[#1F2D4B] rounded-lg p-5 text-white" data-testid="kpi-estimated">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">Por cobrar</span>
                        <CurrencyDollar size={18} weight="duotone" className="text-[#FB7185]" />
                    </div>
                    <p className="font-display text-3xl font-bold">{fmt(pending.estimated_commission)}</p>
                    <p className="text-xs opacity-80 mt-1">
                        {pending.estimated_commission_pending_validation > 0
                            ? `+ ${fmt(pending.estimated_commission_pending_validation)} cuando se validen los pagos`
                            : 'Comisión estimada'}
                    </p>
                </div>
            </div>

            {/* Daily breakdown */}
            <div className="bg-white border border-[#ECE3D2] rounded-lg p-6" data-testid="daily-breakdown-card">
                <h2 className="font-display font-bold text-[#1F2D4B] mb-4 flex items-center gap-2">
                    <Calendar size={18} weight="duotone" /> Desglose por día
                </h2>
                {days.length === 0 ? (
                    <p className="text-sm text-[#8B8474] italic py-6 text-center">Aún no tienes ventas pendientes de comisión.</p>
                ) : (
                    <div className="space-y-3">
                        {days.map(d => {
                            // Suma la comisión SOLO de ventas validadas; el resto es "por confirmar"
                            const dayCommissionValidated = d.items
                                .filter(it => it.payment_validated)
                                .reduce((sum, it) => sum + (it.total_amount || 0) * pending.commission_percent / 100, 0);
                            const dayCommissionPending = d.items
                                .filter(it => !it.payment_validated)
                                .reduce((sum, it) => sum + (it.total_amount || 0) * pending.commission_percent / 100, 0);
                            return (
                            <div key={d.date} className="border border-[#ECE3D2] rounded-md overflow-hidden" data-testid={`day-row-${d.date}`}>
                                <div className="bg-[#F8F4EC] px-4 py-2.5 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-semibold text-[#1F2D4B] capitalize">{fmtDay(d.date)}</p>
                                        <p className="text-[11px] text-[#8B8474]">
                                            {d.count} {d.count === 1 ? 'venta' : 'ventas'} · {d.certificates_count} {d.certificates_count === 1 ? 'certificado' : 'certificados'}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-display text-lg font-bold" style={{ color: '#65C76A' }} data-testid={`day-commission-${d.date}`}>
                                            {fmt(dayCommissionValidated)}
                                        </p>
                                        <p className="text-[10px] text-[#8B8474]">
                                            comisión del día{dayCommissionPending > 0 && (
                                                <> · <span className="text-[#92400E]">+ {fmt(dayCommissionPending)} por validar</span></>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="px-4 py-2 text-xs text-[#4B5878] space-y-1 bg-white">
                                    {d.items.map((it, i) => {
                                        // EF (efectivo) o TT (tarjeta) según método
                                        const pm = (it.payment_method || '').toLowerCase();
                                        const codeMap = {
                                            cash: { code: 'EF', title: 'Efectivo' },
                                            credit_card: { code: 'TT', title: 'Tarjeta' },
                                            debit_card: { code: 'TT', title: 'Tarjeta' },
                                            credit: { code: 'TT', title: 'Tarjeta' },
                                            debit: { code: 'TT', title: 'Tarjeta' },
                                            mixed: { code: 'MX', title: 'Mixto' },
                                            transfer: { code: 'TR', title: 'Transferencia' },
                                        };
                                        const badge = codeMap[pm];
                                        return (
                                        <div key={i} className="flex justify-between items-start gap-3" data-testid={`day-item-${d.date}-${i}`}>
                                            <div className="flex-1 min-w-0">
                                                {(it.folios && it.folios.length > 0) ? (
                                                    <span className="text-[#1F2D4B] font-mono font-semibold">{it.folios.join(', ')}</span>
                                                ) : (
                                                    <span className="text-[#8B8474] italic">Sin folio</span>
                                                )}
                                                {!it.payment_validated && (
                                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] rounded-full bg-amber-100 text-amber-800 font-semibold uppercase tracking-wide" data-testid={`pending-validation-badge-${d.date}-${i}`}>
                                                        Por validar
                                                    </span>
                                                )}
                                            </div>
                                            <span
                                                className={`font-mono font-semibold flex-shrink-0 inline-flex items-baseline gap-1.5 ${it.payment_validated ? 'text-[#65C76A]' : 'text-[#8B8474] line-through'}`}
                                                data-testid={`item-commission-${d.date}-${i}`}
                                            >
                                                {fmt((it.total_amount || 0) * pending.commission_percent / 100)}
                                                {badge && (
                                                    <span
                                                        className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${it.payment_validated ? 'bg-[#65C76A]/15 text-[#3F8A44]' : 'bg-[#8B8474]/15 text-[#8B8474]'}`}
                                                        title={badge.title}
                                                        data-testid={`payment-code-${d.date}-${i}`}
                                                    >
                                                        {badge.code}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                            );
                        })}
                        {/* Totales del corte */}
                        <div className="border-2 border-[#1F2D4B] rounded-md bg-[#1F2D4B] text-white px-4 py-3 flex items-center justify-between" data-testid="commission-totals-row">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">Totales</p>
                                <p className="text-sm font-bold">
                                    {totalSalesCount} {totalSalesCount === 1 ? 'venta' : 'ventas'} · {totalCertsCount} {totalCertsCount === 1 ? 'certificado' : 'certificados'}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">Comisión total estimada</p>
                                <p className="font-display text-xl font-bold" style={{ color: '#FB7185' }}>
                                    {fmt(pending.estimated_commission)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Paid history — sólo visible con permiso `commissions.payment_history_view_own` */}
            {canViewHistory && (
                <div className="bg-white border border-[#ECE3D2] rounded-lg p-6" data-testid="paid-history-card">
                    <h2 className="font-display font-bold text-[#1F2D4B] mb-4 flex items-center gap-2">
                        <Receipt size={18} weight="duotone" /> Historial de pagos
                    </h2>
                    {paidHistory.length === 0 ? (
                        <p className="text-sm text-[#8B8474] italic py-4 text-center">Aún no tienes comisiones pagadas.</p>
                    ) : (
                        <div className="space-y-2">
                            {paidHistory.map(c => (
                                <div key={c.id} className="flex items-center justify-between py-2.5 border-b border-[#ECE3D2] last:border-0" data-testid={`paid-row-${c.id}`}>
                                    <div>
                                        <p className="text-sm font-semibold text-[#1F2D4B]">
                                            Pago del {formatDate(c.paid_at || c.created_at)}
                                        </p>
                                        <p className="text-[11px] text-[#8B8474]">
                                            Periodo: {c.date_from || 'inicio'} → {c.date_to || 'hoy'} · {c.sales_count} activaciones · {PAYMENT_LABELS[c.payment_method] || c.payment_method}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <p className="font-display text-lg font-bold" style={{ color: '#65C76A' }}>{fmt(c.net_amount)}</p>
                                            <span className="status-badge status-paid">Pagado</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MyCommissionsPage;
